import React, { useEffect, useState } from "react";
import { Button, Modal, Row, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CompanyInformation, UserRole } from "../../types";
import { getHasuraRoleContext, isVisitFinlandAdmin } from "../../utils/functions";
import { useLazyQuery, useMutation } from "@apollo/client";
import { CompanyList } from "../../types/companyTypes";
import { useKeycloak } from "@react-keycloak/web";
import getCompanyProductsLite from "../../graphqlQueries/getCompanyProductsLite";
import deleteCompanyAndProductsMutation, {
  DeleteCompanyAndProductsMutationResult,
  DeleteCompanyAndProductsVariables,
} from "../../graphqlQueries/deleteCompanyAndProducts";

type DeleteModalProps = {
  closeModal: (isOnSubmit?: boolean) => void;
  onCancel: () => void;
  companyData?: CompanyList | null;
};

const CompanyDeleteModal = ({ closeModal, onCancel, companyData }: DeleteModalProps) => {
  const { t } = useTranslation();
  const hookFormMethods = useForm<CompanyInformation>({
    defaultValues: {
      companyId: companyData?.ellipsis?.id ?? "",
    },
  });
  const { reset } = hookFormMethods;
  const [isDeleting, setIsDeleting] = useState(false);
  const { keycloak } = useKeycloak();
  const isAdmin = isVisitFinlandAdmin(keycloak);

  const [getCompanyProducts] = useLazyQuery(getCompanyProductsLite);

  const [deleteCompanyAndProducts] = useMutation<
    DeleteCompanyAndProductsMutationResult,
    DeleteCompanyAndProductsVariables
  >(deleteCompanyAndProductsMutation, {
    context: getHasuraRoleContext(UserRole.ManageDatahub),
    onCompleted: (data) => {
      console.log("Deleted product: ", data.updateProduct.returning);
      console.log("Deleted company: ", data.updateCompanyByPk.id);
      closeModal(true);
    },
    onError: (error) => {
      console.error("Error deleting company and products: ", error);
      setIsDeleting(false);
    },
    update(cache, { data }) {
      cache.modify({
        fields: {
          company(existingCompanies, { readField }) {
            const companies = existingCompanies ?? [];
            return companies.filter(
              (companyRef: CompanyList) =>
                readField("id", companyRef) !== data?.updateCompanyByPk.id
            );
          },
        },
      });
    },
  });

  useEffect(() => {
    if (companyData) {
      reset({
        businessId: companyData.businessId || "",
        companyId: companyData.ellipsis?.id || "",
        companyName: companyData.ellipsis?.officialName || "",
      });
    }
  }, [companyData, reset]);

  const submitDelete = async (values: { companyId?: string }) => {
    const { companyId } = values;
    try {
      if (companyId && isAdmin) {
        setIsDeleting(true);
        // Get products associated with companyId
        const result = await getCompanyProducts({
          context: getHasuraRoleContext(UserRole.ManageDatahub),
          fetchPolicy: "network-only",
          variables: { companyId },
        });
        const productIds = result.data?.product.map((p: { id: string }) => p.id) || [];
        // Handle deleting of company and products
        await deleteCompanyAndProducts({
          context: getHasuraRoleContext(UserRole.ManageDatahub),
          fetchPolicy: "network-only",
          variables: {
            id: companyId,
            productId: productIds,
          },
        });
      }
    } catch (e) {
      console.error("Error deleting company:", e);
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      animation={false}
      show
      onHide={onCancel}
      centered
      aria-labelledby="user-management-modal-title"
    >
      <Modal.Header closeButton={true}>
        <h3 id="user-management-modal-title" className="m-0 p-0">
          {t("companyInfoModal.companyDeleteHeader")}
        </h3>
      </Modal.Header>
      <Modal.Body className="px-5 pb-4">
        <div>
          <p>
            <label>{t("companyInfo.officialName")}:</label> {companyData?.ellipsis?.officialName}
          </p>
          <p>
            <label>{t("companyInfo.companyName")}:</label> {companyData?.businessName}
          </p>
          <p>
            <label>{t("companyInfo.businessId")}:</label> {companyData?.businessId}
          </p>
        </div>
        <br />
        <div className="mb-3" style={{ color: "red" }}>
          <label>{t("companyInfoModal.deleteCompanyWarn")}</label>
          <br />- {t("companyInfoModal.deleteCompanyWarnDesc1")}
          <br />- {t("companyInfoModal.deleteCompanyWarnDesc2")}
          <br />- {t("companyInfoModal.deleteCompanyWarnDesc3")}
        </div>
        <Row className="m-0">
          <Button variant="light" className="col mr-3 mt-3" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            className="col mt-3"
            onClick={() => submitDelete({ companyId: companyData?.ellipsis?.id || "" })}
            disabled={isDeleting}
          >
            {isDeleting && <Spinner size="sm" animation="border" />}
            {t("common.delete")}
          </Button>
        </Row>
      </Modal.Body>
    </Modal>
  );
};

export default CompanyDeleteModal;
